import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  X, User, Mail, Phone, MapPin, Fingerprint, Cake, Building2, IdCard,
  Copy, CheckCircle2, RefreshCw, ExternalLink,
} from "lucide-react";

/**
 * PersonalInfoModal — one-glance view of the client's personal info assembled
 * from Close CRM + Jotform + our own DB. Everything is copy-to-clipboard so
 * Xzavier can move fast.
 */

export default function PersonalInfoModal({ clientId, clientEmail, clientName, onOpenFiles, onClose }) {
  const [state, setState] = useState({ loading: false, data: null, err: null });
  const [copied, setCopied] = useState("");

  const load = useCallback(async () => {
    if (!clientId) return;
    setState({ loading: true, data: null, err: null });
    try {
      const params = clientEmail && clientEmail !== clientId ? { email: clientEmail } : undefined;
      const { data } = await api.get(`/admin/clients/${encodeURIComponent(clientId)}/personal-info`, { params });
      setState({ loading: false, data, err: null });
    } catch (e) {
      const err = formatApiError(e.response?.data?.detail) || "Failed to load";
      setState({ loading: false, data: null, err });
      toast.error(err);
    }
  }, [clientId, clientEmail]);
  useEffect(() => { load(); }, [load]);

  const copy = (key, val) => {
    if (!val) return;
    navigator.clipboard.writeText(String(val)).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(""), 1200);
    });
  };

  const d = state.data;
  const close = d?.close || {};
  const jot = d?.jotform || {};
  const local = d?.local;
  const identity = jot.identity || {};
  const address = jot.address || {};
  const business = jot.business || {};

  return (
    <div className="fixed inset-0 z-[120] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 cq-in" data-testid="personal-info-modal">
      <div className="relative w-full max-w-3xl bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="relative p-5 sm:p-6 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
          <div className="cq-grain absolute inset-0 opacity-30" />
          <div className="relative flex items-start justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.32em] font-black text-amber-300 flex items-center gap-1.5">
                <IdCard size={12} /> Personal Info
              </p>
              <h2 className="font-display font-black text-2xl sm:text-3xl mt-1">{clientName || identity.name || "Client"}</h2>
              <p className="text-xs text-slate-300 mt-1">Everything Xzavier needs, pulled from Close, Jotform, and our records.</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={load}
                className="text-slate-300 hover:text-white transition"
                title="Refresh"
                data-testid="pi-refresh"
              >
                <RefreshCw size={16} className={state.loading ? "animate-spin" : ""} />
              </button>
              <button
                onClick={onClose}
                className="text-slate-300 hover:text-white transition"
                data-testid="pi-close"
                aria-label="Close"
              >
                <X size={22} />
              </button>
            </div>
          </div>
          {onOpenFiles && (
            <button
              onClick={() => { onClose && onClose(); onOpenFiles(); }}
              className="relative mt-3 inline-flex items-center gap-1.5 text-[11px] font-bold text-amber-300 hover:text-amber-200"
              data-testid="pi-check-files"
            >
              Check Files <ExternalLink size={11} />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 space-y-5">
          {state.loading && <p className="text-sm text-slate-400 text-center py-10">Loading…</p>}
          {state.err && <p className="text-sm text-rose-600 text-center py-10">{state.err}</p>}
          {!state.loading && !state.err && d && (
            <>
              {/* Identity */}
              <Section title="Identity" icon={Fingerprint} testid="pi-section-identity">
                <Field label="Full name"  value={identity.name || close.lead?.display_name || local?.name} copyKey="name" copy={copy} copied={copied} icon={User} />
                <Field label="Email"      value={d.email || local?.email} copyKey="email" copy={copy} copied={copied} icon={Mail} />
                <Field label="Phone"      value={identity.phone || local?.phone} copyKey="phone" copy={copy} copied={copied} icon={Phone} />
                <Field label="SSN"        value={identity.ssn} copyKey="ssn" copy={copy} copied={copied} icon={Fingerprint} sensitive />
                <Field label="Date of birth" value={identity.dob} copyKey="dob" copy={copy} copied={copied} icon={Cake} />
              </Section>

              {/* Address */}
              {(address.addr_line1 || address.line1 || (close.lead?.addresses || [])[0]) && (
                <Section title="Address" icon={MapPin} testid="pi-section-address">
                  {(() => {
                    const a = address.line1 ? address : (address.addr_line1 ? {
                      line1: address.addr_line1, line2: address.addr_line2, city: address.city,
                      state: address.state, postal: address.postal,
                    } : null);
                    const closeAddr = (close.lead?.addresses || [])[0];
                    const use = a || closeAddr;
                    if (!use) return null;
                    const line = [use.line1 || use.address_1, use.line2 || use.address_2].filter(Boolean).join(", ");
                    const cityLine = [use.city, use.state, use.postal || use.zipcode].filter(Boolean).join(", ");
                    const full = [line, cityLine].filter(Boolean).join(" · ");
                    return (
                      <Field label="Home address" value={full} copyKey="addr" copy={copy} copied={copied} icon={MapPin} multi />
                    );
                  })()}
                </Section>
              )}

              {/* Business */}
              {(business.businessname || business.companyname || business.ein || business.duns || close.lead?.company || business.name) && (
                <Section title="Business" icon={Building2} testid="pi-section-business">
                  <Field label="Business name" value={business.name || business.businessname || business.companyname || close.lead?.company} copyKey="bn" copy={copy} copied={copied} icon={Building2} />
                  <Field label="EIN"           value={business.ein} copyKey="ein" copy={copy} copied={copied} sensitive />
                  <Field label="DUNS"          value={business.duns} copyKey="duns" copy={copy} copied={copied} />
                </Section>
              )}
              {/* Sources footer */}
              <div className="pt-3 border-t border-slate-100 grid grid-cols-3 gap-2 text-[10px] uppercase tracking-widest font-black">
                <SourceChip label="Close CRM"  ok={!!close.lead} testid="pi-src-close" />
                <SourceChip label="Jotform"    ok={(jot.submissions || 0) > 0} count={jot.submissions} testid="pi-src-jotform" />
                <SourceChip label="Our DB"     ok={!!local} testid="pi-src-local" />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 sm:px-6 py-3 border-t border-slate-100 flex justify-end">
          <Button size="sm" variant="outline" onClick={onClose} className="h-8" data-testid="pi-done">Done</Button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, testid, children }) {
  const hasContent = React.Children.toArray(children).some((c) => c);
  if (!hasContent) return null;
  return (
    <div data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.32em] font-black text-slate-400 flex items-center gap-1.5 mb-2">
        <Icon size={11} /> {title}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {children}
      </div>
    </div>
  );
}

function Field({ label, value, copyKey, copy, copied, icon: Icon, sensitive, multi }) {
  if (!value) return null;
  return (
    <button
      onClick={() => copy(copyKey, value)}
      className="group text-left rounded-xl border border-slate-100 hover:border-slate-300 bg-white hover:shadow-sm px-3 py-2 transition"
      data-testid={`pi-field-${copyKey}`}
      title="Click to copy"
    >
      <p className="text-[10px] uppercase tracking-widest font-black text-slate-400 flex items-center gap-1">
        {Icon && <Icon size={10} />} {label}
      </p>
      <div className={`mt-0.5 flex items-center gap-2 ${multi ? "" : "truncate"}`}>
        <span className={`font-bold text-slate-900 ${sensitive ? "font-mono tracking-wider" : ""} ${multi ? "text-sm" : "truncate text-sm"}`}>{value}</span>
        {copied === copyKey
          ? <CheckCircle2 size={11} className="text-emerald-600 shrink-0" />
          : <Copy size={11} className="text-slate-300 group-hover:text-slate-900 shrink-0 transition" />}
      </div>
    </button>
  );
}

function SourceChip({ label, ok, count, testid }) {
  return (
    <span
      className={`inline-flex items-center justify-center gap-1 rounded-full px-2 py-1 border ${ok ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-50 text-slate-400"}`}
      data-testid={testid}
    >
      {ok ? <CheckCircle2 size={9} /> : <X size={9} />}
      {label}{count ? ` · ${count}` : ""}
    </span>
  );
}
